import {
  getQuoteIsKidExpected,
  getQuoteIsKidRequiredByQuoteId,
  getQuoteIsProductGovernanceRequired,
  getQuoteRegulationKidDocument,
  getTargetMarketStatus,
  hasAnyErrorInProductGovernance,
  hasQuoteRegulationKidDocument,
  hasQuoteRegulationKidErrors,
} from 'state/selectors';
import type { AppState } from 'state/state.model';

import { getQuoteType } from '../../selectors';
import { isQuoteTypeWithRegulation } from '../../utils';
import type { RegulationData, RegulationDocumentStatus, SuccessRegulationStatusWithLinks, TileStatus } from '../model';
import { getTabFromQuoteId } from './dashboard.selectors.tabs.helpers';

function getQuoteRegulationKidStatus(state: AppState, tabId: string, quoteId: string): RegulationDocumentStatus {
  const isRequired = getQuoteIsKidRequiredByQuoteId(state, tabId, quoteId);
  if (!isRequired) {
    return 'NotAvailable';
  }
  const isExpected = getQuoteIsKidExpected(state, tabId, quoteId);
  if (!isExpected) {
    return 'NotAvailable';
  }

  const hasErrors = hasQuoteRegulationKidErrors(state, tabId, quoteId);
  if (hasErrors) {
    return 'Failure';
  }
  const isLoaded = hasQuoteRegulationKidDocument(state, tabId, quoteId);
  if (isLoaded) {
    const kidDocuments = getQuoteRegulationKidDocument(state, tabId, quoteId);

    const status: SuccessRegulationStatusWithLinks = {
      status: 'Success',
      links: kidDocuments!.map(doc => [doc.language, doc.uri]),
    };

    return status;
  }

  return 'Pending';
}

function getQuoteRegulationTargetMarketStatus(
  state: AppState,
  tabId: string,
  quoteId: string,
): RegulationDocumentStatus {
  const isRequired = getQuoteIsProductGovernanceRequired(state, tabId);
  if (!isRequired) {
    return 'NotAvailable';
  }
  const hasErrors = hasAnyErrorInProductGovernance(state, tabId, quoteId);
  if (hasErrors) {
    return 'Failure';
  }
  return getTargetMarketStatus(state, tabId, quoteId);
}

const noRegulation = {
  kid: 'NotAvailable',
  tm: 'NotAvailable',
} as RegulationData;

const _getRegulationData = (state: AppState, quoteId: string): RegulationData => {
  const quoteType = getQuoteType(state, quoteId);
  const isRegulationQuote = quoteType !== undefined && isQuoteTypeWithRegulation(quoteType);
  if (!isRegulationQuote) {
    return noRegulation;
  }
  const tab = getTabFromQuoteId(state, quoteId);
  if (tab === undefined || tab.quoteState !== 'Live') {
    return noRegulation;
  }
  return {
    kid: getQuoteRegulationKidStatus(state, tab.tabId, quoteId),
    tm: getQuoteRegulationTargetMarketStatus(state, tab.tabId, quoteId),
  } as RegulationData;
};

export function getRegulationData(state: AppState, status: TileStatus | undefined, quoteId: string): RegulationData {
  switch (status) {
    case 'Quoting':
    case 'Quoted':
    case 'Tradeable':
    case 'Trading':
    case 'Traded':
    case 'RejectedWithPrice':
    case 'TechnicalErrorWithPrice':
      const reloaded = state.quotes.dashboard.reloaded.tiles[quoteId] as RegulationData;
      if (reloaded !== undefined) {
        return { ...noRegulation, ...reloaded };
      }
      return _getRegulationData(state, quoteId);
    default:
      return noRegulation;
  }
}
